import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";

export interface BottomNavItem {
  key: string;
  /** Accessible name. Icon-only rails still need one per destination. */
  label: string;
  icon: ReactNode;
  isActive?: boolean;
  /** A small dot on the icon's corner — unread, needs-attention. */
  showHint?: boolean;
  /** Given `href`, the item renders through `renderLink`; otherwise a button. */
  href?: string;
  onClick?: () => void;
}

export interface BottomNavProps {
  /** The rail's destinations — the caller decides how many (the platform shows 3). */
  items: BottomNavItem[];
  /** Accessible name for the nav landmark, e.g. "Primary". */
  label: string;
  /**
   * Renders a routed item. Apps wire their own router here — the library must
   * not depend on one. Omit and every item falls back to a `<button>`. The
   * element should carry `className`, `style` and `aria-current` as given.
   */
  renderLink?: (
    item: BottomNavItem,
    className: string,
    children: ReactNode,
    style: CSSProperties,
  ) => ReactNode;
  /**
   * The overflow control. When set, the rail grows a More button that unfurls
   * `menu` upward inside the same pill.
   */
  more?: { label: string; icon?: ReactNode };
  /**
   * What More unfurls: identity, every destination, and the route-less
   * actions (language, sign out). Receives `close` so a row can dismiss the
   * menu as it navigates.
   */
  menu?: (close: () => void) => ReactNode;
  /**
   * Whether More wears the active treatment. Defaults to "the page you are on
   * is not one of the rail's", which is how the seller tells you where you are
   * when the rail cannot.
   */
  moreActive?: boolean;
  /** Rendered after the destinations, before More. */
  trailing?: ReactNode;
  className?: string;
}

/** The rail button. Exported so a `trailing` control matches the rest exactly. */
export const bottomNavItemClass =
  "relative flex h-12 w-14 items-center justify-center rounded-full transition-colors [&_svg]:h-[22px] [&_svg]:w-[22px]";

/**
 * The rail's active treatment, as inline style over two overridable tokens.
 *
 * The seller's values are the fallbacks (a neutral filled pill, primary ink); a
 * look that wants another — PAPER's blue — sets `--bottom-nav-active-bg` /
 * `--bottom-nav-active-ink` rather than fighting an inline style.
 */
export function bottomNavItemStyle(isActive?: boolean): CSSProperties {
  return {
    background: isActive ? "var(--bottom-nav-active-bg, var(--soft2))" : "transparent",
    color: isActive
      ? "var(--bottom-nav-active-ink, var(--text-primary))"
      : "var(--bottom-nav-ink, var(--text-muted))",
  };
}

/** The floating shadow, the seller's; a look may set `--bottom-nav-shadow`. */
const floating: CSSProperties = {
  boxShadow: "var(--bottom-nav-shadow, 0 12px 36px rgba(0, 0, 0, 0.16))",
};

/**
 * The platform's mobile navigation, the seller portal's geometry and motion
 * (`apps/seller/src/shared/layout/BottomNav.tsx`), hidden at `lg`.
 *
 * A floating, bottom-anchored pill. Collapsed it is a rail of a few
 * destinations and a More toggle; More grows the pill UPWARD into the full
 * menu — the control you pressed becomes the thing you are reading, anchored
 * to your thumb — while the rail folds away beneath it.
 *
 * The seller's pill shares its row with a Search button. This one has none
 * (the restaurant's founder ruled search out, 2026-10-03), so the pill stands
 * alone, centred, at the width the seller's takes beside its button — not
 * stretched across the screen, which is what the earlier rail did.
 *
 * Only the CHROME is here. Each app supplies its destinations and its menu
 * body, because those are what differ.
 *
 * `pointer-events-none` on the landmark with `pointer-events-auto` on the pill
 * is deliberate — the bar floats over content, and the gaps around it must
 * stay clickable rather than swallowing taps on the page. Positioned with
 * `env(safe-area-inset-bottom)` so it clears the home indicator; the page
 * scroller must pad its own bottom to clear it.
 *
 * The menu closes on a press anywhere outside the pill and on Escape.
 */
export function BottomNav({
  items,
  label,
  renderLink,
  more,
  menu,
  moreActive,
  trailing,
  className = "",
}: BottomNavProps) {
  const [expanded, setExpanded] = useState(false);
  const pillRef = useRef<HTMLDivElement>(null);
  const close = () => setExpanded(false);

  // Outside press and Escape both dismiss. `pointerdown` rather than `click`
  // so the menu is gone before the tap lands on whatever is underneath.
  useEffect(() => {
    if (!expanded) return;
    const onDown = (e: PointerEvent) => {
      if (pillRef.current && !pillRef.current.contains(e.target as Node)) {
        setExpanded(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setExpanded(false);
    };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [expanded]);

  if (items.length === 0 && !trailing && !more) return null;

  const isMoreActive = moreActive ?? (items.length > 0 && !items.some((item) => item.isActive));

  const body = (item: BottomNavItem) => (
    <>
      <span aria-hidden="true" className="inline-flex">
        {item.icon}
      </span>
      {item.showHint ? (
        <span
          aria-hidden="true"
          className="absolute end-2.5 top-2.5 h-2 w-2 rounded-full bg-[color:var(--gold-500)]"
        />
      ) : null}
    </>
  );

  return (
    <nav
      aria-label={label}
      data-ui="bottom-nav"
      className={[
        "no-print pointer-events-none fixed inset-x-0 z-40 flex justify-center px-3 lg:hidden",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      style={{ bottom: "calc(14px + env(safe-area-inset-bottom, 0px))" }}
    >
      {/* 288px is the seller's pill on a 375px phone (the row less its 60px
          Search button and 8px gap); 248px from `sm`, as the seller's. */}
      <div
        ref={pillRef}
        data-ui="bottom-nav-pill"
        data-expanded={expanded ? "true" : undefined}
        className="pointer-events-auto w-full max-w-[18rem] overflow-hidden rounded-[26px] border border-[color:var(--card-br)] bg-[color:var(--card)] sm:max-w-[248px]"
        style={floating}
      >
        {menu ? (
          <div
            data-ui="bottom-nav-menu"
            className={[
              "flex flex-col transition-all duration-300 ease-out",
              expanded
                ? "max-h-[75vh] overflow-y-auto overscroll-contain opacity-100"
                : "max-h-0 overflow-hidden opacity-0",
            ].join(" ")}
            inert={!expanded}
          >
            {menu(close)}
          </div>
        ) : null}

        {/* The rail folds away while the menu is open, as the seller's does. */}
        <div
          data-ui="bottom-nav-rail"
          className={[
            "overflow-hidden transition-all duration-300 ease-out",
            expanded ? "max-h-0 opacity-0" : "max-h-16 opacity-100",
          ].join(" ")}
          inert={expanded}
        >
          <div className="flex items-center justify-between p-1.5">
            {items.map((item) => {
              const inner = body(item);
              const style = bottomNavItemStyle(item.isActive);
              // Rendered directly — `renderLink` is contracted to key its own
              // element, so no wrapper sits in the flex row.
              if (item.href && renderLink) {
                return renderLink(item, bottomNavItemClass, inner, style);
              }
              return (
                <button
                  key={item.key}
                  type="button"
                  aria-label={item.label}
                  aria-current={item.isActive ? "page" : undefined}
                  data-ui="bottom-nav-item"
                  onClick={item.onClick}
                  className={bottomNavItemClass}
                  style={style}
                >
                  {inner}
                </button>
              );
            })}
            {trailing}
            {more ? (
              <button
                type="button"
                aria-label={more.label}
                aria-expanded={expanded}
                data-ui="bottom-nav-more"
                data-active={isMoreActive ? "true" : undefined}
                onClick={() => setExpanded((open) => !open)}
                className={bottomNavItemClass}
                style={bottomNavItemStyle(isMoreActive)}
              >
                {more.icon ?? <ChevronsUpDownGlyph />}
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </nav>
  );
}

const GLYPH = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.8,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
};

/** The default More glyph: two stacked chevrons, the seller's. */
function ChevronsUpDownGlyph() {
  return (
    <svg {...GLYPH} width={22} height={22}>
      <path d="m7 15 5 5 5-5" />
      <path d="m7 9 5-5 5 5" />
    </svg>
  );
}
